import { Handle, Position, type Node as FlowNode, type NodeProps, useReactFlow } from '@xyflow/react';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { AutomationStepIcon } from '../../../components/svgs/icons/AutomationStepIcon';
import { AutomationTemplateStepIndicatorIcon } from '../../../components/svgs/icons/AutomationTemplateStepIndicator';
import type { BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { isAutomationStepNodeData } from '../types/typeGuards';
import { AutomationStepCardAdapter } from './AutomationStepCardAdapter';
import { stepNodeStyles } from './stepNodeStyles';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import { BRANCH_COLORS } from '../edges/branchEdgeStyles';
import { getStepTypeBorderColor } from '../utils/stepTypeColors';

function getStepTypeModelName(stepType: Api.AutomationStepType): Api.AutomationStepModelType {
	const result = (() => {
		switch (stepType) {
			case Api.AutomationStepType.Email:
				return 'EmailAutomationStep';
			case Api.AutomationStepType.Texting:
				return 'TextingAutomationStep';
			case Api.AutomationStepType.ActionItem:
				return 'ActionItemAutomationStep';
			case Api.AutomationStepType.AddTag:
				return 'AddTagAutomationStep';
			case Api.AutomationStepType.RemoveTag:
				return 'RemoveTagAutomationStep';
			case Api.AutomationStepType.HandwrittenCard:
				return 'HandwrittenCardAutomationStep';
			case Api.AutomationStepType.NoAction:
				return 'NoActionAutomationStep';
			case Api.AutomationStepType.Opportunity:
				return 'OpportunityAutomationStep';
			case Api.AutomationStepType.HtmlNewsletter:
				return 'HtmlNewsletterAutomationStep';
			case Api.AutomationStepType.Switch:
				return 'SwitchAutomationStep';
			default:
				return 'EmailAutomationStep';
		}
	})();

	return result;
}

function getDefaultStepName(stepType: Api.AutomationStepType): string {
	switch (stepType) {
		case Api.AutomationStepType.Email:
			return 'Send an email';
		case Api.AutomationStepType.Texting:
			return 'Send a text';
		case Api.AutomationStepType.ActionItem:
			return 'Create an action item';
		case Api.AutomationStepType.AddTag:
			return 'Add a tag';
		case Api.AutomationStepType.RemoveTag:
			return 'Remove a tag';
		case Api.AutomationStepType.HandwrittenCard:
			return 'Send a card';
		case Api.AutomationStepType.NoAction:
			return 'Do nothing';
		case Api.AutomationStepType.Opportunity:
			return 'Create or update opportunity';
		case Api.AutomationStepType.HtmlNewsletter:
			return 'Send HTML newsletter';
		case Api.AutomationStepType.Switch:
			return 'Conditional branch';
		default:
			return 'Automation step';
	}
}

/** Derives the display name for a step node from its pre-computed stepData and type. */
function getStepDisplayName(stepNodeData: AutomationStepNodeData): string {
	const rawStepName = (stepNodeData.stepData?.name || '').trim();

	if (
		stepNodeData.stepType === Api.AutomationStepType.AddTag ||
		stepNodeData.stepType === Api.AutomationStepType.RemoveTag
	) {
		const tagStep = stepNodeData.automationStep as
			| (Api.IAddTagAutomationStep & { _type: 'AddTagAutomationStep' })
			| (Api.IRemoveTagAutomationStep & { _type: 'RemoveTagAutomationStep' })
			| undefined;
		const tagName = tagStep?.tagName?.trim();
		const prefix = stepNodeData.stepType === Api.AutomationStepType.AddTag ? 'Add Tag' : 'Remove Tag';
		if (tagName) {
			return `${prefix}: ${tagName}`;
		}
		return rawStepName || prefix;
	}

	if (stepNodeData.stepType === Api.AutomationStepType.ActionItem) {
		if (!rawStepName) {
			return getDefaultStepName(stepNodeData.stepType);
		}
		if (rawStepName.toLowerCase().startsWith('action item:')) {
			return rawStepName;
		}
		return `Action Item: ${rawStepName}`;
	}

	return rawStepName || getDefaultStepName(stepNodeData.stepType);
}

function formatScheduleSummary(schedule?: Api.IAutomationStepSchedule): string {
	if (!schedule) {
		return 'No schedule set';
	}

	const days = schedule.numberOfDays || 0;
	const hours = schedule.numberOfHours || 0;
	const minutes = schedule.numberOfMinutes || 0;

	// Negative values mean "before trigger", positive means "after trigger"
	const formatUnit = (value: number, unit: string): string => {
		const magnitude = Math.abs(value);
		const plural = magnitude !== 1 ? 's' : '';
		const direction = value < 0 ? 'before' : 'after';
		return `${magnitude} ${unit}${plural} ${direction} trigger`;
	};

	if (days !== 0) {
		return formatUnit(days, 'day');
	}
	if (hours !== 0) {
		return formatUnit(hours, 'hour');
	}
	if (minutes !== 0) {
		return formatUnit(minutes, 'minute');
	}
	return 'Immediately';
}

function CollapseIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M12 10L8 6L4 10' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

function ExpandIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

export function AutomationStepNodeWrapper({ data, id, selected }: NodeProps) {
	// Runtime assertion to ensure data structure is valid
	if (!isAutomationStepNodeData(data)) {
		const debugSnapshot = {
			nodeId: id,
			typeofData: typeof data,
			keys: data && typeof data === 'object' ? Object.keys(data as Record<string, unknown>) : null,
			data,
			stack: new Error('AutomationStepNodeWrapper debug stack').stack,
		};
		console.error('[AutomationStepNodeWrapper] Invalid node data payload', debugSnapshot);
		throw new Error(`Invalid AutomationStepNodeData structure for node ${id}`);
	}

	const stepNodeData = data;
	const [isExpanded, setIsExpanded] = useState(stepNodeData.isExpanded);

	// Sync local state when parent updates data.isExpanded (e.g., Ctrl+E bulk toggle).
	useEffect(() => {
		setIsExpanded(stepNodeData.isExpanded);
	}, [stepNodeData.isExpanded]);

	const { isReadOnly: contextIsReadOnly, updateNodeData } = useAutomationFlowContext();
	const isReadOnly = contextIsReadOnly || stepNodeData.isReadOnly === true;
	const { setNodes } = useReactFlow();

	// Helper to update both local state and persisted node data.
	// When collapsing, clear estimatedHeight so the layout engine does not use a
	// stale expanded height for positioning downstream nodes.
	const setExpanded = useCallback(
		(expanded: boolean) => {
			setIsExpanded(expanded);
			if (expanded) {
				updateNodeData?.(id, { isExpanded: true, isCompact: false });
			} else {
				updateNodeData?.(id, { isExpanded: false, isCompact: true, estimatedHeight: undefined });
			}
		},
		[id, updateNodeData]
	);

	// Ref for measuring content height
	const contentRef = useRef<HTMLDivElement>(null);
	// Use ref to track measured height to avoid recreating ResizeObserver
	const measuredHeightRef = useRef<number | null>(stepNodeData.estimatedHeight || null);
	// Track if initial measurement is done to prevent continuous adjustments
	const initialMeasurementDone = useRef(false);

	const stepTypeName = getStepTypeModelName(stepNodeData.stepType);
	const stepName = getStepDisplayName(stepNodeData);

	// ResizeObserver to measure content height dynamically.
	// Layout adjustment is handled entirely by the canvas-level onNodesChange handler
	// (which reflows on dimension changes). We only need to store the measured height
	// so the layout engine can use accurate dimensions instead of estimates.
	useEffect(() => {
		// Reset initial measurement flag when collapsed
		if (!isExpanded) {
			initialMeasurementDone.current = false;
			return;
		}

		if (!contentRef.current) {
			return;
		}

		const resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				const actualHeight = entry.contentRect.height;

				// Only store the measurement once per expansion to avoid loops.
				// The canvas-level dimension handler will pick up the new size
				// and reflow downstream nodes automatically.
				if (!initialMeasurementDone.current && actualHeight > 0) {
					measuredHeightRef.current = actualHeight;
					initialMeasurementDone.current = true;

					// Single setNodes call: store estimated height so the layout
					// engine uses real measurements instead of guesses.
					setNodes(
						currentNodes =>
							currentNodes.map(node => {
								if (node.id === id) {
									return {
										...node,
										data: {
											...node.data,
											estimatedHeight: actualHeight,
										},
									};
								}
								return node;
							}) as FlowNode<BaseNodeData>[]
					);
				}
			}
		});

		resizeObserver.observe(contentRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [id, isExpanded, setNodes]);

	const contactColors = stepNodeData.contactColors;
	const contactCount = stepNodeData.contactCount;

	// Support multiple output handles for switch nodes
	const outputHandles = stepNodeData.outputHandles || ['default'];
	const hasMultipleOutputs = outputHandles.length > 1;
	const sourcePosition = stepNodeData.sourcePosition || Position.Bottom;
	const targetPosition = stepNodeData.targetPosition || Position.Top;
	const isHorizontal = sourcePosition === Position.Left || sourcePosition === Position.Right;

	// Calculate handle positions for multiple outputs
	const calculateHandleStyle = (index: number, total: number) => {
		if (total === 1) {
			return isHorizontal ? { top: '50%' } : { left: '50%' };
		}
		const percentage = (100 / (total + 1)) * (index + 1);
		return isHorizontal ? { top: `${percentage}%` } : { left: `${percentage}%` };
	};

	// Get parent case info for visual indicator
	const parentCaseInfo = stepNodeData.parentCaseInfo;
	const isInvalid = !stepNodeData.isValid;

	// Border color priority: invalid state (red from CSS class) > case color > step type color
	// Only apply inline border color when not invalid, so the CSS class can set red border
	const borderColor = parentCaseInfo?.caseColor || getStepTypeBorderColor(stepNodeData.stepType);
	const cardBorderStyle: CSSProperties | undefined = isInvalid ? undefined : { borderColor };

	const scheduleText = stepNodeData.stepData?.schedule ? formatScheduleSummary(stepNodeData.stepData.schedule) : '';
	const ariaLabel = `${stepName} automation step${scheduleText ? `, scheduled ${scheduleText}` : ''}${isInvalid ? ', configuration incomplete' : ''}`;
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			setExpanded(!isExpanded);
		}
	};

	// Helper function to get case color
	const getCaseColor = (caseIndex: number): string => {
		const switchStep = stepNodeData.automationStep as Api.ISwitchAutomationStep | undefined;
		const cases = switchStep?.cases || [];
		const caseStatement = cases[caseIndex];

		// Check if this is the default case
		if (caseStatement?.isDefault) {
			return '#6B7280'; // Gray for default
		}

		return BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
	};

	// Helper function to get case name
	const getCaseName = (caseIndex: number): string => {
		const switchStep = stepNodeData.automationStep as Api.ISwitchAutomationStep | undefined;
		const cases = switchStep?.cases || [];
		const caseStatement = cases[caseIndex];

		if (caseStatement?.isDefault) {
			return 'Default case';
		}

		return caseStatement?.name || `Case ${caseIndex + 1}`;
	};

	// Render compact switch info
	const renderCompactSwitchInfo = () => {
		if (stepNodeData.stepType !== Api.AutomationStepType.Switch || !stepNodeData.outputHandles) {
			return null;
		}

		const caseCount = stepNodeData.outputHandles.length;
		const casesToShow = Math.min(caseCount, 4);
		const overflow = Math.max(0, caseCount - 4);

		return (
			<div className={css(stepNodeStyles.switchCompactInfo)}>
				<div className={css(stepNodeStyles.switchBranchCount)}>
					{caseCount} {caseCount === 1 ? 'branch' : 'branches'}
				</div>
				<div className={css(stepNodeStyles.switchCaseIndicators)}>
					{stepNodeData.outputHandles.slice(0, casesToShow).map((handleId, index) => (
						<div
							key={handleId}
							className={css(stepNodeStyles.switchCaseDot)}
							style={{ backgroundColor: getCaseColor(index) }}
							aria-label={getCaseName(index)}
						/>
					))}
					{overflow > 0 && <span className={css(stepNodeStyles.switchOverflow)}>+{overflow}</span>}
				</div>
			</div>
		);
	};

	return (
		<div
			className={css(stepNodeStyles.nodeContainer, selected && stepNodeStyles.selected)}
			role='button'
			tabIndex={0}
			aria-label={ariaLabel}
			aria-expanded={isExpanded}
			aria-describedby={`step-description-${id}`}
			onKeyPress={handleKeyPress}
		>
			<Handle type='target' position={targetPosition} id='target' className={css(stepNodeStyles.handle)} />

			{/* Visual indicator showing which case this child node belongs to */}
			{parentCaseInfo && (
				<div
					className={css(stepNodeStyles.parentCaseIndicator)}
					style={{
						backgroundColor: parentCaseInfo.caseColor || BRANCH_COLORS[0],
					}}
					aria-label={`Part of ${parentCaseInfo.isDefault ? 'default case' : `Segment #${(parentCaseInfo.caseIndex || 0) + 1}`}`}
				>
					{parentCaseInfo.isDefault ? 'DEFAULT' : `Segment #${(parentCaseInfo.caseIndex || 0) + 1}`}
				</div>
			)}

			{contactColors && contactColors.length > 0 && (
				<div
					className={css(stepNodeStyles.contactBadgeContainer)}
					aria-label={`${contactCount || contactColors.length} contacts at this step`}
				>
					{contactColors.slice(0, 3).map((color, index) => (
						<div
							key={index}
							className={css(stepNodeStyles.contactColorDot)}
							style={{
								backgroundColor: color,
							}}
							aria-hidden='true'
						/>
					))}
					{(contactCount || contactColors.length) > 3 && (
						<span className={css(stepNodeStyles.contactOverflow)}>+{(contactCount || contactColors.length) - 3}</span>
					)}
				</div>
			)}

			{isExpanded ? (
				<div
					ref={contentRef}
					className={`${css(stepNodeStyles.expandedCard, isInvalid && stepNodeStyles.invalid)} nopan`}
					style={cardBorderStyle}
					id={`step-description-${id}`}
				>
					<div className={css(stepNodeStyles.expandedHeader)}>
						<div className={css(stepNodeStyles.headerLeft)}>
							{stepTypeName === 'SwitchAutomationStep' ? (
								<AutomationTemplateStepIndicatorIcon
									step={{ _type: stepTypeName } as Api.IAutomationStep}
									type='default'
								/>
							) : (
								<AutomationStepIcon type={stepTypeName} />
							)}
							<span className={css(stepNodeStyles.headerTitle)} title={stepName}>
								{stepName}
							</span>
							{isReadOnly && (
								<div className={css(stepNodeStyles.readOnlyBadge)} title='Read-only mode' aria-label='Read-only mode'>
									READ-ONLY
								</div>
							)}
						</div>
						<button
							className={css(stepNodeStyles.collapseButton)}
							onClick={() => {
								setExpanded(false);
							}}
							aria-label='Collapse step details'
							title='Collapse'
						>
							<CollapseIcon />
						</button>
					</div>

					<div className={css(stepNodeStyles.expandedContent)}>
						<AutomationStepCardAdapter nodeId={id} data={stepNodeData} mode='full' readOnly={isReadOnly} />
					</div>
				</div>
			) : (
				<div
					ref={contentRef}
					className={css(stepNodeStyles.compactCard, isInvalid && stepNodeStyles.invalid)}
					style={cardBorderStyle}
					onClick={() => {
						setExpanded(true);
					}}
					id={`step-description-${id}`}
				>
					<div className={css(stepNodeStyles.compactHeader)}>
						{stepTypeName === 'SwitchAutomationStep' ? (
							<AutomationTemplateStepIndicatorIcon step={{ _type: stepTypeName } as Api.IAutomationStep} type='small' />
						) : (
							<AutomationStepIcon type={stepTypeName} />
						)}
						<span className={css(stepNodeStyles.compactTitle)} title={stepName}>
							{stepName}
						</span>
						<button
							className={css(stepNodeStyles.expandButton)}
							onClick={e => {
								e.stopPropagation();
								setExpanded(true);
							}}
							aria-label='Expand step details'
							title='Expand'
						>
							<ExpandIcon />
						</button>
					</div>

					{stepNodeData.stepData?.schedule && (
						<div className={css(stepNodeStyles.compactSchedule)}>
							{formatScheduleSummary(stepNodeData.stepData.schedule)}
						</div>
					)}

					{renderCompactSwitchInfo()}
				</div>
			)}

			{/* Render multiple source handles for switch nodes */}
			{outputHandles.map((handleId: string, index: number) => (
				<Handle
					key={handleId}
					type='source'
					position={sourcePosition}
					id={handleId}
					className={css(stepNodeStyles.handle)}
					style={hasMultipleOutputs ? calculateHandleStyle(index, outputHandles.length) : undefined}
				/>
			))}
		</div>
	);
}
